import { useState } from 'react';
import type { InputTableCellParams, InputTableRowParams } from '@/components/custom/erp';
import type { ThueGiayBaoNo } from '@/types/schemas';

export interface SelectedCellInfo {
  id: string;
  field: string;
}

export interface ThueRow extends Omit<ThueGiayBaoNo, 'uuid' | 'ma_thue_data'> {
  uuid: string;
  line?: number;
  ma_thue_data?:
    | (NonNullable<ThueGiayBaoNo['ma_thue_data']> & {
        thue_suat?: ThueGiayBaoNo['thue_suat'];
      })
    | null;
}

export function useThueRows(
  initialRows: ThueRow[] = [],
  onChange?: (rows: ThueRow[]) => void,
  onFieldValueChange?: (row: ThueRow, field: string, value: unknown) => Partial<ThueRow>
) {
  const [rows, setRows] = useState<ThueRow[]>(initialRows && initialRows.length > 0 ? initialRows : []);
  const [selectedRowUuid, setSelectedRowUuid] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<ThueRow | null>(null);
  const [selectedCell, setSelectedCell] = useState<SelectedCellInfo | null>(null);

  const handleRowClick = (params: InputTableRowParams<ThueRow>) => {
    const rowUuid = params.id?.toString() || params.row?.uuid;
    if (!rowUuid) return;

    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row as ThueRow);
  };

  const handleCellClick = (params: InputTableCellParams) => {
    const rowUuid = params.id.toString();
    if (!rowUuid) return;

    setSelectedRowUuid(rowUuid);
    setSelectedRow(params.row);

    setSelectedCell({
      id: rowUuid,
      field: params.field
    });
  };

  const clearSelection = () => {
    setSelectedRowUuid(null);
    setSelectedRow(null);
    setSelectedCell(null);
  };

  const handleCellValueChange = (rowUuid: string, field: string, newValue: ThueRow[keyof ThueRow]) => {
    const rowIndex = rows.findIndex(row => row.uuid === rowUuid);
    if (rowIndex === -1) return;

    const currentRow = rows[rowIndex];
    const extraFields = onFieldValueChange?.(currentRow, field, newValue) ?? {};

    let updatedRow = {
      ...currentRow,
      [field]: newValue,
      ...extraFields
    };

    if (field === 't_tien_nt' || field === 'ma_thue_data') {
      const tTienNt = field === 't_tien_nt' ? Number(newValue) || 0 : Number(updatedRow.t_tien_nt) || 0;
      const thueSuat =
        field === 'ma_thue_data'
          ? Number((newValue as ThueRow['ma_thue_data'])?.thue_suat) || 0
          : Number(updatedRow.ma_thue_data?.thue_suat) || 0;

      const tThueNt = (tTienNt * thueSuat) / 100;
      updatedRow = {
        ...updatedRow,
        t_thue_nt: Math.round(tThueNt * 10) / 10
      };
    }

    const updatedRows = [...rows];
    updatedRows[rowIndex] = updatedRow;

    setRows(updatedRows);
    onChange?.(updatedRows);

    if (rowUuid === selectedRowUuid) {
      setSelectedRow(updatedRow);
    }
  };

  const handleAddRow = () => {
    const newRow: ThueRow = {
      uuid: `temp-${Date.now()}`,
      ...(rows.length > 0 && rows[0].line !== undefined && { line: rows.length + 1 })
    };

    const newRows = [...rows, newRow];
    setRows(newRows);
    onChange?.(newRows);

    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handleDeleteRow = () => {
    if (!selectedRowUuid) return;

    const updatedRows = rows.filter((row: ThueRow) => row.uuid !== selectedRowUuid);

    const reorderedRows = updatedRows.map((row: ThueRow, index: number) => ({
      ...row,
      ...(row.line !== undefined && { line: index + 1 })
    }));

    setRows(reorderedRows);
    onChange?.(reorderedRows);

    if (reorderedRows.length > 0) {
      const lastRow = reorderedRows[reorderedRows.length - 1];
      setSelectedRowUuid(lastRow.uuid);
      setSelectedRow(lastRow);
    } else {
      clearSelection();
    }
  };

  const handleCopyRow = () => {
    if (!selectedRow) return;

    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    const copiedRow: ThueRow = {
      ...rowDataWithoutUuid,
      uuid: `temp-${Date.now()}`,
      ...(selectedRow.line !== undefined && { line: rows.length + 1 })
    };

    const newRows = [...rows, copiedRow];
    setRows(newRows);
    onChange?.(newRows);

    setSelectedRowUuid(copiedRow.uuid);
    setSelectedRow(copiedRow);
  };

  const handlePasteRow = () => {
    if (!selectedRow) return;

    const { uuid, ...rowDataWithoutUuid } = selectedRow;

    const newRow: ThueRow = {
      ...rowDataWithoutUuid,
      uuid: `temp-${Date.now()}`,
      ...(selectedRow.line !== undefined && { line: rows.length + 1 })
    };

    let newRows: ThueRow[] = [];

    if (selectedRowUuid) {
      const selectedIndex = rows.findIndex(row => row.uuid === selectedRowUuid);
      if (selectedIndex === -1) return;

      newRows = [...rows];
      newRows.splice(selectedIndex + 1, 0, newRow);
    } else {
      newRows = [...rows, newRow];
    }

    setRows(newRows);
    onChange?.(newRows);

    setSelectedRowUuid(newRow.uuid);
    setSelectedRow(newRow);
  };

  const handleMoveRow = (direction: 'up' | 'down') => {
    if (!selectedRowUuid) return;

    const currentIndex = rows.findIndex((row: ThueRow) => row.uuid === selectedRowUuid);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? Math.max(0, currentIndex - 1) : Math.min(rows.length - 1, currentIndex + 1);

    if (newIndex === currentIndex) return;

    const newRows = [...rows];
    const [movedRow] = newRows.splice(currentIndex, 1);
    newRows.splice(newIndex, 0, movedRow);

    newRows.forEach((row: ThueRow, index: number) => {
      if (row.line !== undefined) {
        row.line = index + 1;
      }
    });

    setRows(newRows);
    onChange?.(newRows);

    setSelectedRow(movedRow);
  };

  return {
    rows,
    setRows,
    selectedRowUuid,
    selectedRow,
    selectedCell,
    handleRowClick,
    handleCellClick,
    clearSelection,
    handleCellValueChange,
    handleAddRow,
    handleDeleteRow,
    handleCopyRow,
    handlePasteRow,
    handleMoveRow
  };
}
